import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-success text-text hover:bg-success/90 disabled:opacity-50',
  secondary: 'border border-border bg-surface text-text hover:bg-surface-elevated',
  ghost: 'bg-transparent text-text hover:bg-surface-elevated/85',
  danger: 'bg-danger text-primary-foreground hover:bg-danger/90',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-10 px-4 text-sm rounded-pill',
  md: 'h-12 px-5 text-sm rounded-pill',
  lg: 'h-14 px-6 text-base rounded-pill',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'primary', size = 'md', isLoading, className, disabled, children, ...rest },
    ref,
  ) => (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold tracking-tight transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {isLoading ? <span className="animate-pulse">…</span> : children}
    </button>
  ),
);
Button.displayName = 'Button';
